import {
  AutoStack,
  DayPickerStrings,
  FormikGraphQLDatePicker,
  onFormatDate,
  OrderIdPicker,
} from 'components'
import { FormikTagPicker } from 'components/TagPicker/FormikTagPicker'
import { NotificationDialog } from 'dialogs/NotificationDialog'
import { FieldArray, FieldProps, FormikConsumer } from 'formik'
import {
  FormikDropdown,
  FormikSpinButton,
  FormikTextField,
} from 'formik-office-ui-fabric-react'
import { AuthorizedField as Field } from 'forms/AuthorizedField'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { Box } from 'grid'
import { useDialogState } from 'hooks/useDialogState'
import _ from 'lodash'
import {
  OrderIdDistribution,
  Proving,
  ResourceRequirementAddInput,
  ResourceRequirementBatchInput,
} from 'model'
import {
  DayOfWeek,
  IconButton,
  IDropdownOption,
  ITag,
  Label,
} from 'office-ui-fabric-react'
import { Position } from 'office-ui-fabric-react/lib/utilities/positioning'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

export type ProvingDetailsResourceRequirementFormValues =
  ResourceRequirementAddInput
export type ProvingDetailsResourceRequirementFormUpdateValues =
  ResourceRequirementBatchInput
export class ProvingDetailsResourceRequirementFormSchema {
  constructor() {
    return Yup.object().shape({
      resourceRequirementType: Yup.string(),
      orderIdDistribution: Yup.array().when('resourceRequirementType', {
        is: (type) => ['accessCard', 'fuelCard', 'radio'].includes(type),
        then: Yup.array()
          .of(
            Yup.object().shape({
              orderId: Yup.string().required(),
            })
          )
          .required(),
        otherwise: Yup.array().when('resourceRequirementType', {
          is: 'testTrack',
          then: Yup.array()
            .required()
            .test(
              'sum',
              'Die Summe der Auftragsnummern muss 100% betragen',
              (value: OrderIdDistribution[]) => {
                return (
                  value.reduce((sum, x) => sum + (x.percentage || 0), 0) === 100
                )
              }
            ),
          otherwise: Yup.array().notRequired(),
        }),
      }),
      dateFrom: Yup.date().required(),
      dateTo: Yup.date()
        .when(
          'dateFrom',
          (dateFrom: any, schema: any) => dateFrom && schema.min(dateFrom)
        )
        .required(),
      testTrack: Yup.string().when('resourceRequirementType', {
        is: (type) => type === 'testTrack',
        then: Yup.string().required(),
        otherwise: Yup.string().notRequired(),
      }),
      racewayHours: Yup.number(),
      // notification: Yup.string(),
      // notificationAcknowledged: Yup.bool().when('notification', {
      //   is: (notification: any, schema: any) => notification && schema.length(),
      //   then: Yup.bool().oneOf([true]),
      //   otherwise: Yup.bool(),
      // }),
      // .when('resourceRequirementType', {
      //   is: type => type === 'testTrack',
      //   then: Yup.number()
      //     .required()
      //     .min(1),
      //   otherwise: Yup.number().notRequired(),
      // }),
    })
  }
}

const resetForm = (fieldProps: FieldProps<any>) => {
  fieldProps.form.setFieldValue('orderIdDistribution', [])
  fieldProps.form.setFieldValue('amount', 1)
  fieldProps.form.setFieldValue('testTrack', '')
  fieldProps.form.setFieldTouched('testTrack', false)
}

export const ProvingDetailsResourceRequirementFormWithContext: React.FC<{
  proving: Partial<Proving>
  testTracks: any
  testTrackUsages: any
  fuels: any
  chargingInfrastructures: any
  ctx: any
}> = ({
  proving,
  testTracks,
  testTrackUsages,
  fuels,
  chargingInfrastructures,
  ctx,
}) => {
  const { t } = useTranslation()
  const [selectedType, setSelectedType] = React.useState()
  const [selectedTestTrack, setTestTrack] = React.useState()
  const [noteState, setNoteState] = React.useState()
  const [dialogState, showDialog] = useDialogState()
  const [, setFuel] = React.useState()
  const [, setChargingInfrastructure] = React.useState()
  const orderIds = proving.orderIdDistribution!.map((oId: any) => oId.orderId)
  const orderIdOptions = [
    ...proving.orderIdDistribution!.map(
      (orderId: OrderIdDistribution) =>
        ({
          key: orderId.orderId,
          text: `${orderId.orderId} - ${orderId.annotation || ''} ${
            orderId.percentage ? `(${orderId.percentage} %)` : ''
          }`,
        } as IDropdownOption)
    ),
  ]

  const orderIdOptionsNoEmpty = orderIds.map(
    (orderId: any) =>
      ({
        key: orderId,
        text: orderId,
      } as IDropdownOption)
  )

  const allTheTags = testTrackUsages
    .filter((ttu: any) => ttu.parentId === selectedTestTrack)
    .map((testTrackUsage: any) => ({
      key: testTrackUsage._id,
      name: testTrackUsage.title,
    })) as ITag[]

  // const testTracktest = selectedTestTrack
  //   ? testTracks.find((tt: any) => tt._id === selectedTestTrack)
  //   : undefined

  // const note =
  //   testTracktest && testTracktest.notification
  //     ? testTracktest.notification
  //     : ''

  // const getTestTrackNotification = () => {
  //   let testTrackNotification = ''
  //   testTracks.filter((testTrack: any) => {
  //     if (testTrack._id === selectedTestTrack) {
  //       testTrackNotification = testTrack.notification
  //     }
  //   })

  //   return testTrackNotification
  // }

  // const setTextFieldValue = (fieldProps: any, ttn: string) => {
  //   fieldProps.form.setFieldValue('notification', ttn)
  // }

  const percentages: IDropdownOption[] = []
  _.times(10, (i) => {
    percentages.push({ key: (10 - i) * 10, text: `${(10 - i) * 10} %` })
  })
  const resourceRequirementTypeOptions: IDropdownOption[] = [
    { key: 'workstation', text: t('dropdown.option.rrWorkstation') },
    { key: 'garage', text: t('dropdown.option.rrGarage') },
    { key: 'liftingPlatform', text: t('dropdown.option.rrLiftingPlatform') },
    {
      key: 'groundWorkStation',
      text: t('dropdown.option.rrGroundWorkStation'),
    },
    { key: 'coldCell', text: t('dropdown.option.rrColdCell') },
    { key: 'testTrack', text: t('dropdown.option.rrTestTrack') },
    { key: 'parkingSpace', text: t('dropdown.option.rrParkingSpace') },
    { key: 'accessCard', text: t('dropdown.option.rrAccessCard') },
    { key: 'fuelCard', text: t('dropdown.option.rrFuelCard') },
    { key: 'radio', text: t('dropdown.option.rrRadio') },
  ]

  if (fuels.length > 0) {
    resourceRequirementTypeOptions.push({
      key: 'fuel',
      text: t('dropdown.option.rrFuel'),
    })
  }
  if (chargingInfrastructures.length > 0) {
    resourceRequirementTypeOptions.push({
      key: 'chargingInfrastructure',
      text: t('dropdown.option.rrChargingInfrastructure'),
    })
  }

  return (
    <>
      <FormStack>
        <Label>
          {t('model.proving.resourceRequirements.addResourceRequirement')}
        </Label>
        <FieldStack horizontal={true}>
          <FieldStackItem
            name={'resourceRequirementType'}
            render={(fieldProps) => (
              <FormikDropdown
                label={'Typ'}
                required={true}
                options={resourceRequirementTypeOptions}
                onChanged={(option) => {
                  setSelectedType(option.key)
                  resetForm(fieldProps)
                }}
                {...fieldProps}
              />
            )}
          />
          {selectedType &&
            selectedType !== 'testTrack' &&
            selectedType &&
            selectedType !== 'fuel' &&
            selectedType &&
            selectedType !== 'chargingInfrastructure' && (
              <FieldStackItem
                name={'amount'}
                render={(fieldProps) => (
                  <FormikSpinButton
                    label={'Anzahl'}
                    labelPosition={Position.top}
                    min={1}
                    max={30}
                    step={1}
                    {...fieldProps}
                  />
                )}
              />
            )}
        </FieldStack>
        {selectedType && (
          <>
            <FieldStack>
              {selectedType === 'fuel' && (
                <>
                  <FieldStackItem
                    name={'fuel'}
                    render={(fieldProps) => (
                      <FormikTagPicker
                        tags={
                          fuels.map((fuel: any) => ({
                            key: fuel._id,
                            name: fuel.title,
                          })) as ITag[]
                        }
                        onChanged={async (tt) => {
                          await setFuel(tt.key)
                        }}
                        label="Kraftstoff"
                        {...fieldProps}
                      />
                    )}
                  />
                  <FieldStackItem
                    name={'amount'}
                    render={(fieldProps) => (
                      <FormikTextField
                        label={'Menge in Liter'}
                        type="number"
                        {...fieldProps}
                      />
                    )}
                  />
                </>
              )}
              {selectedType === 'chargingInfrastructure' && (
                <>
                  <FieldStackItem
                    name={'chargingInfrastructure'}
                    render={(fieldProps) => (
                      <FormikTagPicker
                        tags={
                          chargingInfrastructures.map((charger: any) => ({
                            key: charger._id,
                            name: charger.title,
                          })) as ITag[]
                        }
                        onChanged={async (tt) => {
                          await setChargingInfrastructure(tt.key)
                        }}
                        label="Ladeinfrastruktur"
                        {...fieldProps}
                      />
                    )}
                  />
                  <FieldStackItem
                    name={'amount'}
                    render={(fieldProps) => (
                      <FormikTextField
                        type="number"
                        label={'Menge in kWh'}
                        {...fieldProps}
                      />
                    )}
                  />
                </>
              )}
              {selectedType === 'testTrack' && (
                <>
                  <FieldStackItem
                    name={'testTrack'}
                    render={(fieldProps) => (
                      <FormikTagPicker
                        tags={
                          testTracks.map((testTrack: any) => ({
                            key: testTrack._id,
                            name: testTrack.title,
                          })) as ITag[]
                        }
                        onChanged={(tt) => {
                          setTestTrack(tt.key)
                          const testTrack = tt.key
                            ? testTracks.find((tty: any) => tty._id === tt.key)
                            : undefined

                          const note =
                            testTrack && testTrack.notification
                              ? testTrack.notification
                              : ''
                          if (note) {
                            setNoteState(note)
                            showDialog()
                          }
                        }}
                        label="Strecke"
                        {...fieldProps}
                      />
                    )}
                  />
                  {allTheTags.length > 0 && (
                    <FieldStackItem
                      name={'testTrackUsage'}
                      render={(fieldProps) => (
                        <FormikTagPicker
                          tags={allTheTags}
                          label="Streckennutzung"
                          {...fieldProps}
                        />
                      )}
                    />
                  )}
                </>
              )}
            </FieldStack>
            <FieldStack horizontal={true}>
              <FieldStackItem
                name={'dateFrom'}
                render={(fieldProps) => (
                  <FormikGraphQLDatePicker
                    label={'Von'}
                    defaultValue={proving.dateFrom}
                    // minDate={new Date(proving.dateFrom)}
                    // maxDate={new Date(proving.dateTo)}
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={DayPickerStrings}
                    showWeekNumbers={true}
                    formatDate={onFormatDate as any}
                    {...fieldProps}
                    initialPickerDate={new Date(proving.dateFrom)}
                  />
                )}
              />
              <FieldStackItem
                name={'dateTo'}
                render={(fieldProps) => (
                  <FormikGraphQLDatePicker
                    label={'Bis'}
                    defaultValue={proving.dateTo}
                    // minDate={new Date(proving.dateFrom)}
                    // maxDate={new Date(proving.dateTo)}
                    firstDayOfWeek={DayOfWeek.Monday}
                    strings={DayPickerStrings}
                    showWeekNumbers={true}
                    formatDate={onFormatDate as any}
                    {...fieldProps}
                    initialPickerDate={new Date(proving.dateFrom)}
                  />
                )}
              />
            </FieldStack>
            {proving.provingGround!.racewayHours &&
              selectedType === 'testTrack' && (
                <>
                  <FieldStack>
                    <Label required={true}>
                      Ungefähre Anzahl durchschnittliche Streckennutzung je
                      Fahrzeug und Tag in Stunden
                    </Label>
                  </FieldStack>
                  <FieldStack>
                    <FieldStackItem
                      name="racewayHours"
                      required={true}
                      render={(fieldProps) => (
                        <FormikSpinButton
                          label={t('Stunden')}
                          labelPosition={Position.top}
                          min={0}
                          max={24}
                          step={1}
                          {...fieldProps}
                        />
                      )}
                    />
                  </FieldStack>
                </>
              )}
            {['accessCard', 'fuelCard', 'radio'].includes(selectedType) && (
              <FieldStack horizontal={true}>
                <FieldStackItem
                  name={'orderIdDistribution[0].orderId'}
                  render={(fieldProps) => (
                    <FormikDropdown
                      label={t('field.orderId.label')}
                      required={true}
                      {...fieldProps}
                      options={orderIdOptions}
                    />
                  )}
                />
              </FieldStack>
            )}{' '}
            {selectedType === 'testTrack' && (
              <>
                <Label required={true}>{'Auftragsnummern'}</Label>
                <FieldArray name="orderIdDistribution">
                  {({ form, name, ...helpers }) => {
                    const values: {
                      orderId: string
                      percentage: number
                    }[] = form.values[name] || []

                    return (
                      <AutoStack tokens={{ childrenGap: 4 }}>
                        {values.map((x: any, i: any) => (
                          <AutoStack
                            horizontal={true}
                            verticalAlign="center"
                            tokens={{ childrenGap: 8 }}
                            key={i}
                          >
                            <Field
                              name={`orderIdDistribution[${i}].orderId`}
                              render={(props: any) => (
                                <FormikDropdown
                                  styles={{ dropdown: { width: 175 } }}
                                  placeholders={{
                                    default: t(
                                      'section.orderIds.field.orderId'
                                    ),
                                  }}
                                  options={
                                    i !== 0
                                      ? orderIdOptions
                                      : orderIdOptionsNoEmpty
                                  }
                                  {...props}
                                />
                              )}
                            />
                            <Field
                              name={`orderIdDistribution[${i}].percentage`}
                              render={(props: any) => (
                                <FormikSpinButton
                                  min={0}
                                  label="%"
                                  labelPosition={Position.end}
                                  max={100}
                                  step={5}
                                  {...props}
                                />
                              )}
                            />
                            <IconButton
                              styles={{ root: { color: 'red' } }}
                              iconProps={{ iconName: 'Delete' }}
                              onClick={() => helpers.remove(i)}
                            />
                          </AutoStack>
                        ))}
                        <OrderIdPicker
                          blacklist={values
                            .filter((val) =>
                              orderIdOptions.find(
                                (tag) => tag.key === val.orderId
                              )
                            )
                            .map((v) => v.orderId)}
                          styles={{ text: { width: 175 } }}
                          inputProps={{
                            placeholder:
                              '+ ' + t('section.orderIds.field.orderId'),
                          }}
                          initialValues={orderIds}
                          itemLimit={1}
                          onItemSelected={(item) => {
                            if (item) {
                              helpers.push({
                                orderId: item.key,
                                percentage:
                                  100 -
                                  values.reduce(
                                    (sum, x) => sum + x.percentage,
                                    0
                                  ),
                                annotation: '',
                              })
                              return null
                            }
                            return null
                          }}
                        />
                        <AutoStack
                          horizontal={true}
                          verticalAlign="center"
                          tokens={{ childrenGap: 8 }}
                        >
                          <Box
                            width="175px"
                            style={{ textAlign: 'right' }}
                            px={1}
                          >
                            {t('section.orderIds.field.sum')}
                          </Box>
                          <Box px={1}>
                            {values.reduce((sum, x) => sum + x.percentage, 0)}%
                          </Box>
                        </AutoStack>
                        <div style={{ color: 'red' }}>
                          {form.errors.orderIdDistribution}
                        </div>
                      </AutoStack>
                    )
                  }}
                </FieldArray>
                {/* {getTestTrackNotification() && (
                <FieldStack>
                  <Stack.Item>
                    <Text font="xLarge" mt={2} mb={1}>
                      {t('field.specialNote.label')}
                    </Text>
                  </Stack.Item>
                  <Stack.Item>
                    <div>{getTestTrackNotification()}</div>
                  </Stack.Item>
                </FieldStack>
              )} */}

                {/* {bla() && bla().length > 0 && (
                <FieldStackItem
                  name="notification"
                  render={(fieldProps: FieldProps<any>) => (
                    <FormikTextField
                      multiline={true}
                      rows={5}
                      {...fieldProps}
                      disabled={true}
                    />
                  )}
                />
              )} */}

                {/* {bla() && bla().length > 0 && (
                <FieldStackItem
                  name="notificationAcknowledged"
                  render={(fieldProps: FieldProps<any>) => (
                    <Checkbox
                      label={t('Hinweis zur Kenntnis genommen')}
                      onChange={(i, isChecked) => {
                        fieldProps.form.setFieldValue(
                          'notificationAcknowledged',
                          isChecked
                        )
                      }}
                    />
                  )}
                />
              )} */}
              </>
            )}
          </>
        )}
      </FormStack>
      <>
        {noteState && (
          <NotificationDialog
            {...dialogState}
            titleText={'Streckenhinweis'}
            subText={noteState}
          />
        )}
      </>
    </>
  )
}
export const ProvingDetailsResourceRequirementForm: React.FC<{
  proving: Partial<Proving>
  testTracks: any
  testTrackUsages: any
  fuels: any
  chargingInfrastructures: any
}> = ({
  proving,
  testTracks,
  testTrackUsages,
  fuels,
  chargingInfrastructures,
}) => (
  <FormikConsumer>
    {(ctx) => (
      <ProvingDetailsResourceRequirementFormWithContext
        proving={proving}
        testTracks={testTracks}
        ctx={ctx}
        testTrackUsages={testTrackUsages}
        fuels={fuels}
        chargingInfrastructures={chargingInfrastructures}
      />
    )}
  </FormikConsumer>
)
